import { LocalStorageScratchpadRepository } from '../infrastructure/repositories/LocalStorageScratchpadRepository.js';
import { getLanguage, t } from './i18n.js';
import { getOperatingStatus } from './storage.js';
import { getTranslatedCenterName, getTranslatedAddress } from './translator.js';
import { DEFAULT_CHECKLIST_PRESETS } from '../domain/scratchpad/TravelScratchpad.js';
import { AffiliateService } from '../infrastructure/services/AffiliateService.js';

let scratchpadState = null;
let currentSubTab = 'spots'; // 'spots', 'checklist', 'expenses', 'converter', 'phrases'
let allCentersData = [];
let onCenterClickCallback = null;

/**
 * Initialize Scratchpad View with centers dataset
 */
export function initScratchpadView(centers, onCenterClick) {
  allCentersData = centers || [];
  onCenterClickCallback = onCenterClick;
  scratchpadState = LocalStorageScratchpadRepository.load();
}

/**
 * Get current scratchpad instance
 */
export function getScratchpadInstance() {
  if (!scratchpadState) {
    scratchpadState = LocalStorageScratchpadRepository.load();
  }
  return scratchpadState;
}

/**
 * Save current scratchpad instance
 */
export function saveScratchpadInstance() {
  if (scratchpadState) {
    LocalStorageScratchpadRepository.save(scratchpadState);
  }
}

/**
 * Render the entire Scratchpad Container View
 */
export function renderScratchpadView(containerId = 'scratchpad-view-container') {
  const container = document.getElementById(containerId);
  if (!container) return;

  const sp = getScratchpadInstance();
  const currentLang = getLanguage();

  container.innerHTML = `
    <div class="scratchpad-header-card glass-panel">
      <div class="scratchpad-title-wrap">
        <h3 class="scratchpad-main-title">${t('scratchpadTitle')}</h3>
        <p class="scratchpad-subtitle">${t('scratchpadSubtitle')}</p>
      </div>
      
      <!-- Sub Tab Bar -->
      <div class="scratchpad-subtabs">
        <button class="subtab-btn ${currentSubTab === 'spots' ? 'active' : ''}" data-subtab="spots">
          ${t('scratchpadSpotsTab')} <span class="badge-count">${sp.items.length}</span>
        </button>
        <button class="subtab-btn ${currentSubTab === 'checklist' ? 'active' : ''}" data-subtab="checklist">
          ${t('scratchpadChecklistTab')}
        </button>
        <button class="subtab-btn ${currentSubTab === 'expenses' ? 'active' : ''}" data-subtab="expenses">
          ${t('scratchpadExpenseTab')}
        </button>
        <button class="subtab-btn ${currentSubTab === 'converter' ? 'active' : ''}" data-subtab="converter">
          ${t('scratchpadConverterTab')}
        </button>
        <button class="subtab-btn ${currentSubTab === 'phrases' ? 'active' : ''}" data-subtab="phrases">
          ${t('scratchpadPhrasesTab')}
        </button>
      </div>
    </div>

    <div class="scratchpad-subview-content" id="scratchpad-subview-content">
      <!-- Dynamically filled by currentSubTab -->
    </div>
  `;

  // Bind Subtab events
  container.querySelectorAll('.subtab-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      currentSubTab = btn.dataset.subtab;
      renderScratchpadView(containerId);
    });
  });

  // Render the selected subview
  renderSubView();
}

function renderSubView() {
  const contentEl = document.getElementById('scratchpad-subview-content');
  if (!contentEl) return;

  if (currentSubTab === 'spots') {
    renderSpotsSubView(contentEl);
  } else if (currentSubTab === 'checklist') {
    renderChecklistSubView(contentEl);
  } else if (currentSubTab === 'expenses') {
    renderExpensesSubView(contentEl);
  } else if (currentSubTab === 'converter') {
    renderConverterSubView(contentEl);
  } else if (currentSubTab === 'phrases') {
    renderPhrasesSubView(contentEl);
  }
}

// -------------------------------------------------------------
// 1. SPOTS / ITINERARY SUBVIEW
// -------------------------------------------------------------
function renderSpotsSubView(container) {
  const sp = getScratchpadInstance();
  const currentLang = getLanguage();

  if (sp.items.length === 0) {
    container.innerHTML = `
      <div class="empty-state glass-panel" style="padding: 32px 20px; text-align: center;">
        <p style="font-size: 2.2rem; margin-bottom: 12px;">📝</p>
        <p style="color: var(--text-muted); font-size: 0.95rem; line-height: 1.6;">
          ${t('emptyScratchpad')}
        </p>
      </div>
    `;
    return;
  }

  const visitedCount = sp.items.filter(it => it.isVisited).length;

  let spotsHtml = `
    <div class="scratchpad-toolbar glass-panel">
      <div class="toolbar-stats">
        <span>${t('spotsProgress', { total: sp.items.length, visited: visitedCount })}</span>
      </div>
      <button class="btn btn-sm btn-primary" id="btn-export-itinerary">
        ${t('exportItinerary')}
      </button>
    </div>
    <div class="scratchpad-spots-list">
  `;

  sp.items.forEach((item, index) => {
    const center = allCentersData.find(c => c.id === item.centerId);
    if (!center) return;

    const op = getOperatingStatus(center);
    const displayName = getTranslatedCenterName(center, currentLang);
    const displayAddress = getTranslatedAddress(center, currentLang);
    const isFirst = index === 0;
    const isLast = index === sp.items.length - 1;

    // Kakao / Naver / Google directions URL
    const kakaoMapUrl = `https://map.kakao.com/link/to/${encodeURIComponent(center.name)},${center.lat},${center.lng}`;
    const naverMapUrl = `https://map.naver.com/v5/search/${encodeURIComponent(center.name)}`;
    const googleMapUrl = `https://www.google.com/maps/dir/?api=1&destination=${center.lat},${center.lng}`;

    spotsHtml += `
      <div class="scratchpad-spot-card glass-panel ${item.isVisited ? 'visited' : ''}" data-id="${center.id}">
        <div class="spot-card-top">
          <div class="spot-order-pill">#${item.order}</div>
          <div class="spot-card-meta">
            <span class="status-chip chip-${op.status}">${t(op.textKey)} (${op.hours})</span>
          </div>
          <div class="spot-card-ctrls">
            <button class="btn-ctrl btn-move-up" data-id="${center.id}" title="${t('moveUp')}" ${isFirst ? 'disabled' : ''}>⬆️</button>
            <button class="btn-ctrl btn-move-down" data-id="${center.id}" title="${t('moveDown')}" ${isLast ? 'disabled' : ''}>⬇️</button>
            <button class="btn-ctrl btn-delete-spot" data-id="${center.id}" title="${t('deleteSpot')}">🗑️</button>
          </div>
        </div>

        <h4 class="spot-card-title">${displayName}</h4>
        <p class="spot-card-address">📍 ${displayAddress}</p>

        <!-- User Custom Note Area -->
        <div class="spot-note-box">
          <textarea class="spot-note-input" data-id="${center.id}" placeholder="${t('memoPlaceholder')}" rows="2">${item.customNote || ''}</textarea>
          <div class="spot-note-footer">
            <span class="note-saved-status" id="status-${center.id}"></span>
            <button class="btn-note-save" data-id="${center.id}">${t('saveMemo')}</button>
          </div>
        </div>

        <!-- Card Bottom Actions -->
        <div class="spot-card-bottom">
          <div class="spot-dir-links">
            <button class="dir-badge btn-popup-taxi" data-id="${center.id}" style="background: #F59E0B; color: #FFF; border: none; cursor: pointer; font-weight: 700;">🚖 Taxi</button>
            <a href="${kakaoMapUrl}" target="_blank" rel="noopener noreferrer" class="dir-badge kakao">${t('btnKakao')}</a>
            <a href="${naverMapUrl}" target="_blank" rel="noopener noreferrer" class="dir-badge naver">${t('btnNaver')}</a>
            <a href="${googleMapUrl}" target="_blank" rel="noopener noreferrer" class="dir-badge google">${t('btnGoogle')}</a>
          </div>
          <button class="btn-toggle-visited ${item.isVisited ? 'is-visited' : ''}" data-id="${center.id}">
            ${item.isVisited ? '✅ ' + t('visited') : '⭕ ' + t('notVisited')}
          </button>
        </div>
      </div>
    `;
  });

  spotsHtml += `</div>`;
  container.innerHTML = spotsHtml;

  // Bind Events for Spots View
  const exportBtn = container.querySelector('#btn-export-itinerary');
  if (exportBtn) {
    exportBtn.addEventListener('click', () => {
      exportItineraryToClipboard();
    });
  }

  container.querySelectorAll('.btn-move-up').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      sp.moveSpot(btn.dataset.id, 'up');
      saveScratchpadInstance();
      renderSpotsSubView(container);
    });
  });

  container.querySelectorAll('.btn-move-down').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      sp.moveSpot(btn.dataset.id, 'down');
      saveScratchpadInstance();
      renderSpotsSubView(container);
    });
  });

  container.querySelectorAll('.btn-delete-spot').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      sp.removeSpot(btn.dataset.id);
      saveScratchpadInstance();
      renderScratchpadView();
    });
  });

  container.querySelectorAll('.btn-note-save').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.id;
      const input = container.querySelector(`.spot-note-input[data-id="${id}"]`);
      if (input) {
        sp.updateSpotNote(id, input.value);
        saveScratchpadInstance();
        const statusSpan = container.querySelector(`#status-${id}`);
        if (statusSpan) {
          statusSpan.textContent = t('saved');
          statusSpan.style.color = '#10B981';
          setTimeout(() => { statusSpan.textContent = ''; }, 2000);
        }
      }
    });
  });

  container.querySelectorAll('.btn-toggle-visited').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.id;
      sp.toggleVisited(id);
      saveScratchpadInstance();
      renderSpotsSubView(container);
    });
  });

  container.querySelectorAll('.spot-card-title').forEach(title => {
    title.addEventListener('click', () => {
      const card = title.closest('.scratchpad-spot-card');
      const id = card.dataset.id;
      const center = allCentersData.find(c => c.id === id);
      if (center && onCenterClickCallback) {
        onCenterClickCallback(center);
      }
    });
  });
}

function exportItineraryToClipboard() {
  const sp = getScratchpadInstance();
  const currentLang = getLanguage();

  let text = `[K-TourSpot] ✈️ ${t('scratchpadTitle')}\n`;
  text += `====================================\n\n`;

  sp.items.forEach((item, idx) => {
    const center = allCentersData.find(c => c.id === item.centerId);
    if (!center) return;
    const displayName = getTranslatedCenterName(center, currentLang);
    const displayAddress = getTranslatedAddress(center, currentLang);
    const status = item.isVisited ? `[${t('visited')}]` : `[${t('notVisited')}]`;

    text += `${idx + 1}. ${displayName} ${status}\n`;
    text += `   - ${t('address')}: ${displayAddress}\n`;
    if (item.customNote) {
      text += `   - Memo: ${item.customNote}\n`;
    }
    text += `\n`;
  });

  text += `\n[${t('scratchpadChecklistTab')}]\n`;
  sp.checklists.forEach(chk => {
    const presetDef = DEFAULT_CHECKLIST_PRESETS.find(p => p.id === chk.id);
    const displayText = (chk.category === 'preset' && presetDef) ? t(presetDef.text) : chk.text;
    text += `${chk.completed ? '[v]' : '[ ]'} ${displayText}\n`;
  });

  if (sp.expenses.length > 0) {
    text += `\n[${t('expenseTitle')} - ${t('expenseTotal')}: ₩${sp.getTotalExpense().toLocaleString()}]\n`;
    sp.expenses.forEach(exp => {
      text += `- ${exp.title}: ₩${Number(exp.amount).toLocaleString()}\n`;
    });
  }

  text += `\nOfficial Website: https://k-tour.web.app\n`;

  navigator.clipboard.writeText(text).then(() => {
    alert(t('itineraryCopied'));
  }).catch(() => {
    alert(text);
  });
}

// -------------------------------------------------------------
// 2. CHECKLIST SUBVIEW WITH NATIVE AFFILIATE HOOKS
// -------------------------------------------------------------
function renderChecklistSubView(container) {
  const sp = getScratchpadInstance();
  const total = sp.checklists.length;
  const completedCount = sp.checklists.filter(c => c.completed).length;
  const progressPercent = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  // Preset affiliate quick link mapping
  const presetAffiliateLinks = {
    pre_esim: AffiliateService.getTrackedUrl('airalo', 'scratchpad_chk'),
    pre_luggage: AffiliateService.getTrackedUrl('goodlugg', 'scratchpad_chk'),
    pre_transit: AffiliateService.getTrackedUrl('kkday', 'scratchpad_chk')
  };

  let html = `
    <div class="checklist-progress-card glass-panel">
      <div class="progress-header">
        <span>${t('checklistProgress')}</span>
        <strong>${completedCount} / ${total} (${progressPercent}%)</strong>
      </div>
      <div class="progress-bar-track">
        <div class="progress-bar-fill" style="width: ${progressPercent}%;"></div>
      </div>
    </div>

    <!-- Add Item Form -->
    <div class="checklist-add-bar glass-panel">
      <input type="text" id="input-new-checklist" class="input-custom" placeholder="${t('addChecklistPlaceholder')}">
      <button class="btn btn-primary" id="btn-add-checklist">${t('addBtn')}</button>
    </div>

    <!-- Checklist Items -->
    <div class="checklist-items-wrap">
  `;

  sp.checklists.forEach(item => {
    const presetDef = DEFAULT_CHECKLIST_PRESETS.find(p => p.id === item.id);
    const displayText = (item.category === 'preset' && presetDef) ? t(presetDef.text) : item.text;
    const affLink = presetAffiliateLinks[item.id];

    html += `
      <div class="checklist-row glass-panel ${item.completed ? 'completed' : ''}" data-id="${item.id}">
        <label class="checklist-label">
          <input type="checkbox" class="chk-toggle" data-id="${item.id}" ${item.completed ? 'checked' : ''}>
          <span class="chk-custom-box"></span>
          <span class="chk-text">${displayText}</span>
        </label>
        <div class="chk-right-actions">
          ${affLink ? `
            <a href="${affLink}" target="_blank" rel="noopener noreferrer" class="btn-chk-deal" title="${t('dealApplyNow')}">
              ⚡ ${t('dealApplyNow')}
            </a>
          ` : ''}
          <button class="btn-chk-delete" data-id="${item.id}" title="${t('deleteSpot')}">&times;</button>
        </div>
      </div>
    `;
  });

  html += `
    </div>
    <div class="checklist-footer-actions">
      <button class="btn btn-sm btn-secondary" id="btn-reset-presets">
        🔄 ${t('resetPresets')}
      </button>
    </div>
  `;

  container.innerHTML = html;

  const addInput = container.querySelector('#input-new-checklist');
  const addBtn = container.querySelector('#btn-add-checklist');
  const handleAdd = () => {
    if (addInput && addInput.value.trim()) {
      sp.addChecklist(addInput.value);
      saveScratchpadInstance();
      renderChecklistSubView(container);
    }
  };

  if (addBtn) addBtn.addEventListener('click', handleAdd);
  if (addInput) {
    addInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') handleAdd();
    });
  }

  container.querySelectorAll('.chk-toggle').forEach(chk => {
    chk.addEventListener('change', () => {
      sp.toggleChecklist(chk.dataset.id);
      saveScratchpadInstance();
      renderChecklistSubView(container);
    });
  });

  container.querySelectorAll('.btn-chk-delete').forEach(btn => {
    btn.addEventListener('click', () => {
      sp.removeChecklist(btn.dataset.id);
      saveScratchpadInstance();
      renderChecklistSubView(container);
    });
  });

  const resetBtn = container.querySelector('#btn-reset-presets');
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      if (confirm(t('confirmResetPresets'))) {
        sp.resetChecklistPresets(t);
        saveScratchpadInstance();
        renderChecklistSubView(container);
      }
    });
  }
}

// -------------------------------------------------------------
// 3. EXPENSES SUBVIEW WITH SAVINGS PROMO BOX
// -------------------------------------------------------------
function renderExpensesSubView(container) {
  const sp = getScratchpadInstance();
  const total = sp.getTotalExpense();
  const klookUrl = AffiliateService.getTrackedUrl('klook', 'scratchpad_expense');

  let html = `
    <div class="expense-summary-card glass-panel">
      <span class="summary-label">${t('expenseTotal')}</span>
      <h2 class="summary-total-amount">₩ ${total.toLocaleString()}</h2>
    </div>

    <!-- Expense Savings Monetization Box -->
    <div class="expense-savings-box glass-panel">
      <div class="savings-icon">💡</div>
      <div class="savings-text-wrap">
        <p class="savings-title">${t('expenseSavingsTip')}</p>
        <a href="${klookUrl}" target="_blank" rel="noopener noreferrer" class="btn-savings-cta">
          ${t('dealTours')} ➔
        </a>
      </div>
    </div>

    <!-- Add Expense Form -->
    <div class="expense-add-card glass-panel">
      <div class="form-row">
        <select id="select-expense-cat" class="select-custom">
          <option value="food">${t('catFood')}</option>
          <option value="transport">${t('catTransport')}</option>
          <option value="ticket">${t('catTicket')}</option>
          <option value="shopping">${t('catShopping')}</option>
          <option value="etc">${t('catEtc')}</option>
        </select>
        <input type="text" id="input-expense-title" class="input-custom" placeholder="${t('expenseItemPlaceholder')}">
      </div>
      <div class="form-row mt-8">
        <input type="number" id="input-expense-amount" class="input-custom" placeholder="${t('expenseAmountPlaceholder')}">
        <button class="btn btn-primary" id="btn-add-expense">${t('addBtn')}</button>
      </div>
    </div>

    <!-- Expense List -->
    <div class="expense-list-wrap">
  `;

  if (sp.expenses.length === 0) {
    html += `
      <div class="empty-state glass-panel" style="padding: 24px; text-align: center;">
        <p style="color: var(--text-muted); font-size: 0.9rem;">${t('noExpenses')}</p>
      </div>
    `;
  } else {
    const categoryIcons = {
      food: '🍴',
      transport: '🚇',
      ticket: '🎟️',
      shopping: '🛍️',
      etc: '📦'
    };

    sp.expenses.forEach(exp => {
      const icon = categoryIcons[exp.category] || '📦';
      html += `
        <div class="expense-row glass-panel" data-id="${exp.id}">
          <div class="expense-row-left">
            <span class="cat-icon-badge">${icon}</span>
            <div class="expense-row-info">
              <strong class="exp-title">${exp.title}</strong>
              <small class="exp-date">${new Date(exp.createdAt).toLocaleDateString()}</small>
            </div>
          </div>
          <div class="expense-row-right">
            <span class="exp-amount">₩ ${Number(exp.amount).toLocaleString()}</span>
            <button class="btn-exp-delete" data-id="${exp.id}" title="${t('deleteSpot')}">&times;</button>
          </div>
        </div>
      `;
    });
  }

  html += `</div>`;
  container.innerHTML = html;

  const titleInput = container.querySelector('#input-expense-title');
  const amountInput = container.querySelector('#input-expense-amount');
  const catSelect = container.querySelector('#select-expense-cat');
  const addBtn = container.querySelector('#btn-add-expense');

  const handleAddExpense = () => {
    const title = titleInput ? titleInput.value.trim() : '';
    const amount = amountInput ? Number(amountInput.value) : 0;
    const cat = catSelect ? catSelect.value : 'food';

    if (!title || !amount || amount <= 0) {
      alert(t('enterExpenseValid'));
      return;
    }

    sp.addExpense(title, amount, cat);
    saveScratchpadInstance();
    renderExpensesSubView(container);
  };

  if (addBtn) addBtn.addEventListener('click', handleAddExpense);

  container.querySelectorAll('.btn-exp-delete').forEach(btn => {
    btn.addEventListener('click', () => {
      sp.removeExpense(btn.dataset.id);
      saveScratchpadInstance();
      renderExpensesSubView(container);
    });
  });
}

// -------------------------------------------------------------
// 4. CURRENCY CONVERTER SUBVIEW WITH PROMO RIBBON
// -------------------------------------------------------------
function renderConverterSubView(container) {
  const RATES = {
    USD: 1380,
    JPY: 9.2,
    CNY: 190
  };

  const airaloUrl = AffiliateService.getTrackedUrl('airalo', 'scratchpad_conv');

  let html = `
    <div class="converter-card glass-panel">
      <h3 class="converter-title">${t('converterTitle')}</h3>
      <p class="converter-notice">${t('exchangeRateNotice')}</p>

      <div class="converter-input-grid">
        <div class="converter-field">
          <label>🇰🇷 ${t('krw')}</label>
          <div class="input-with-unit">
            <input type="number" id="conv-krw" class="conv-input" value="10000">
            <span class="unit-tag">₩</span>
          </div>
        </div>

        <div class="converter-field">
          <label>🇺🇸 ${t('usd')}</label>
          <div class="input-with-unit">
            <input type="number" id="conv-usd" class="conv-input" step="0.01">
            <span class="unit-tag">$</span>
          </div>
        </div>

        <div class="converter-field">
          <label>🇯🇵 ${t('jpy')}</label>
          <div class="input-with-unit">
            <input type="number" id="conv-jpy" class="conv-input" step="1">
            <span class="unit-tag">¥</span>
          </div>
        </div>

        <div class="converter-field">
          <label>🇨🇳 ${t('cny')}</label>
          <div class="input-with-unit">
            <input type="number" id="conv-cny" class="conv-input" step="0.1">
            <span class="unit-tag">¥</span>
          </div>
        </div>
      </div>

      <!-- Quick Preset Buttons -->
      <div class="converter-presets">
        <span class="preset-label">${t('quickAmount')}:</span>
        <button class="btn-preset" data-krw="10000">${t('preset10k')}</button>
        <button class="btn-preset" data-krw="50000">${t('preset50k')}</button>
        <button class="btn-preset" data-krw="100000">${t('preset100k')}</button>
        <button class="btn-preset" data-krw="500000">${t('preset500k')}</button>
      </div>

      <!-- Monetization Benefit Ribbon -->
      <div class="converter-benefit-ribbon">
        <span class="benefit-text">💸 ${t('converterBenefit')}</span>
        <a href="${airaloUrl}" target="_blank" rel="noopener noreferrer" class="btn-benefit-link">
          ${t('dealApplyNow')} ➔
        </a>
      </div>
    </div>
  `;

  container.innerHTML = html;

  const krwEl = container.querySelector('#conv-krw');
  const usdEl = container.querySelector('#conv-usd');
  const jpyEl = container.querySelector('#conv-jpy');
  const cnyEl = container.querySelector('#conv-cny');

  const updateFromKrw = (krwVal) => {
    const krw = Math.max(0, Number(krwVal) || 0);
    usdEl.value = (krw / RATES.USD).toFixed(2);
    jpyEl.value = Math.round(krw / RATES.JPY);
    cnyEl.value = (krw / RATES.CNY).toFixed(1);
  };

  const updateFromUsd = (usdVal) => {
    const usd = Math.max(0, Number(usdVal) || 0);
    const krw = usd * RATES.USD;
    krwEl.value = Math.round(krw);
    jpyEl.value = Math.round(krw / RATES.JPY);
    cnyEl.value = (krw / RATES.CNY).toFixed(1);
  };

  const updateFromJpy = (jpyVal) => {
    const jpy = Math.max(0, Number(jpyVal) || 0);
    const krw = jpy * RATES.JPY;
    krwEl.value = Math.round(krw);
    usdEl.value = (krw / RATES.USD).toFixed(2);
    cnyEl.value = (krw / RATES.CNY).toFixed(1);
  };

  const updateFromCny = (cnyVal) => {
    const cny = Math.max(0, Number(cnyVal) || 0);
    const krw = cny * RATES.CNY;
    krwEl.value = Math.round(krw);
    usdEl.value = (krw / RATES.USD).toFixed(2);
    jpyEl.value = Math.round(krw / RATES.JPY);
  };

  krwEl.addEventListener('input', () => updateFromKrw(krwEl.value));
  usdEl.addEventListener('input', () => updateFromUsd(usdEl.value));
  jpyEl.addEventListener('input', () => updateFromJpy(jpyEl.value));
  cnyEl.addEventListener('input', () => updateFromCny(cnyEl.value));

  updateFromKrw(10000);

  container.querySelectorAll('.btn-preset').forEach(btn => {
    btn.addEventListener('click', () => {
      const val = btn.dataset.krw;
      krwEl.value = val;
      updateFromKrw(val);
    });
  });
}

// -------------------------------------------------------------
// 5. SURVIVAL PHRASES SUBVIEW
// -------------------------------------------------------------
function renderPhrasesSubView(container) {
  const phrases = [
    {
      korean: '관광안내소가 어디에 있나요?',
      pron: 'gwan-gwang-an-nae-so-ga eo-di-e iss-na-yo?',
      en: 'Where is the tourist information center?',
      ja: '観光案内所はどこですか？',
      zh: '请问旅游咨询处在哪里？'
    },
    {
      korean: '짐을 맡길 수 있나요? (짐보관)',
      pron: 'jim-eul mat-gil su iss-na-yo?',
      en: 'Can I store my luggage here?',
      ja: '荷物を預けられますか？',
      zh: '请问这里可以寄存行李吗？'
    },
    {
      korean: '택스리펀(면세) 되나요?',
      pron: 'taeg-seu-li-peon-deu doe-na-yo?',
      en: 'Can I get a tax refund here?',
      ja: '免税(タックスリファンド)はできますか？',
      zh: '请问这里可以办理退税吗？'
    },
    {
      korean: '1330 통역 안내원 연결해 주세요.',
      pron: 'il-sam-sam-gong tong-yeok yeon-gyeol-hae ju-se-yo.',
      en: 'Please connect me to 1330 translation hotline.',
      ja: '1330日本語通訳につないでください。',
      zh: '请帮我接通 1330 中文翻译热线。'
    },
    {
      korean: '이거 얼마인가요?',
      pron: 'i-geo eol-ma-in-ga-yo?',
      en: 'How much is this?',
      ja: 'これいくらですか？',
      zh: '请问这个多少钱？'
    },
    {
      korean: '안 맵게 해주세요 (덜 맵게).',
      pron: 'an maep-ge hae-ju-se-yo.',
      en: 'Please make it mild / not spicy.',
      ja: '辛くしないでください（控えめに）。',
      zh: '请不要放辣（少辣）。'
    },
    {
      korean: '티머니/교통카드 충전해 주세요.',
      pron: 'T-money chung-jeon-hae ju-se-yo.',
      en: 'Please top up my T-Money transit card.',
      ja: 'T-moneyカードのチャージをお願いします。',
      zh: '请帮我充值 T-Money 交通卡。'
    },
    {
      korean: '화장실이 어디에 있나요?',
      pron: 'hwa-jang-sil-i eo-di-e iss-na-yo?',
      en: 'Where is the restroom?',
      ja: 'お手洗いはどこですか？',
      zh: '请问洗手间在哪里？'
    }
  ];

  const currentLang = getLanguage();

  let html = `
    <div class="phrases-header-card glass-panel">
      <h3>🗣️ ${t('phrasesTitle')}</h3>
      <p style="color: var(--text-muted); font-size: 0.85rem;">${t('clickPhraseToCopy')}</p>
    </div>
    <div class="phrases-grid">
  `;

  phrases.forEach(p => {
    let subTranslation = p.en;
    if (currentLang === 'ja') subTranslation = p.ja;
    else if (currentLang === 'zh') subTranslation = p.zh;

    html += `
      <div class="phrase-card glass-panel" data-korean="${p.korean}">
        <div class="phrase-korean-wrap">
          <h4 class="phrase-korean">"${p.korean}"</h4>
          <span class="phrase-copy-badge">${t('copy')}</span>
        </div>
        <p class="phrase-pron">🗣️ ${p.pron}</p>
        <p class="phrase-trans">🌐 ${subTranslation}</p>
      </div>
    `;
  });

  html += `</div>`;
  container.innerHTML = html;

  container.querySelectorAll('.phrase-card').forEach(card => {
    card.addEventListener('click', () => {
      const korean = card.dataset.korean;
      navigator.clipboard.writeText(korean).then(() => {
        const badge = card.querySelector('.phrase-copy-badge');
        if (badge) {
          badge.textContent = t('copied');
          badge.style.background = '#10B981';
          badge.style.color = '#fff';
          setTimeout(() => {
            badge.textContent = t('copy');
            badge.style.background = '';
            badge.style.color = '';
          }, 2000);
        }
      });
    });
  });
}
